import React, { useCallback, useState } from 'react';
import {
  Box,
  CheckBox,
  OptionKey,
  Preloader,
  SelectDropdown,
  Text,
} from '@common-fe/common-fe';

import AppButton from '@/components/controls/AppButton';
import { CARD_LOCKED_REASONS_OPTIONS } from '@/modules/transaction/Cards/Cards.constants';

interface Props {
  isLoading?: boolean;
  onCancel: () => void;
  onConfirm: (params: { lockReason: OptionKey; isEmployeeUnlockForbidden: boolean }) => void;
}

const LockCardConfirmDialog: React.FC<Props> = ({ isLoading, onCancel, onConfirm }) => {
  const [lockReason, setLockReason] = useState<OptionKey>('');
  const [isEmployeeUnlockForbidden, setIsEmployeeUnlockForbidden] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleConfirm = useCallback(() => {
    if (!lockReason) {
      setShowError(true);
      return;
    }
    onConfirm({ lockReason, isEmployeeUnlockForbidden });
  }, [lockReason, isEmployeeUnlockForbidden, onConfirm]);

  return (
    <Box direction="column" data-testid="lock-card-confirm-dialog">
      <Box
        background="module"
        pad="spacing24"
        round="moduleRound"
      >
        <Box
          direction="row"
          align="center"
          justify="between"
          background="canvas"
          pad="spacing24"
          round="container1Round"
          border={{ color: 'border2', size: 'small' }}
        >
          <Text size="14px" color="textBody" weight={700}>
            <Text color="danger">*</Text>
            {' '}
            Lock reason
          </Text>
          <Box width="320px" margin={{ left: 'xs' }}>
            <SelectDropdown
              id="cardLockReason"
              testId="lock-card-confirm-dialog_lock-reason"
              name="cardLockReason"
              placeholder="Select lock reason"
              singleMode
              options={CARD_LOCKED_REASONS_OPTIONS}
              value={lockReason}
              errorText={showError && !lockReason ? 'This field is required' : undefined}
              onChange={(value) => {
                setShowError(false);
                setLockReason(value);
              }}
            />
          </Box>
        </Box>
      </Box>
      <Box direction="row" align="center" justify="between" margin={{ top: 'l' }}>
        <CheckBox
          name="isEmployeeUnlockForbidden"
          role="checkbox"
          aria-label="isEmployeeUnlockForbidden"
          checked={isEmployeeUnlockForbidden}
          onChange={({ target }) => setIsEmployeeUnlockForbidden(target.checked)}
          label={<Text style={{ fontSize: 14, lineHeight: '24px' }}>Prevent employee from card unlock</Text>}
        />
        <Box direction="row" align="center" gap="s">
          <AppButton
            buttonType="secondary"
            width="140px"
            type="button"
            testId="lock-card-confirm-dialog_cancel-button"
            onClick={onCancel}
          >
            Cancel
          </AppButton>
          <AppButton
            disabled={isLoading}
            type="button"
            color="red"
            width="140px"
            testId="lock-card-confirm-dialog_confirm-button"
            onClick={handleConfirm}
          >
            {isLoading ? <Preloader color="white" /> : 'Lock'}
          </AppButton>
        </Box>
      </Box>
    </Box>
  );
};

export default LockCardConfirmDialog;
